import React, { useEffect, useState } from 'react';
import { getLscAdminIssues, LscApiError } from '../../api';
import LoadingState from '../LoadingState';
import { LSC_ADMIN_BLOCKED_MESSAGE, LSC_ADMIN_BLOCKED_TITLE } from '../../adminIssueCopy';

export type LscAdminAccessState = 'loading' | 'allowed' | 'blocked' | 'error';

export function useLscAdminAccess(): {
  accessState: LscAdminAccessState;
  errorMessage: string | null;
} {
  const [accessState, setAccessState] = useState<LscAdminAccessState>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void getLscAdminIssues()
      .then(() => {
        if (!cancelled) {
          setAccessState('allowed');
          setErrorMessage(null);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof LscApiError && (err.status === 403 || err.code === 'admin_forbidden')) {
          setAccessState('blocked');
          setErrorMessage(null);
          return;
        }
        setAccessState('error');
        setErrorMessage(
          err instanceof LscApiError
            ? err.message
            : 'LSC admin tools could not be loaded. Try again in a moment.'
        );
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { accessState, errorMessage };
}

export function LscAdminAccessGate({ children }: { children: React.ReactNode }) {
  const { accessState, errorMessage } = useLscAdminAccess();

  if (accessState === 'loading') {
    return <LoadingState message="Checking LSC admin access..." />;
  }

  if (accessState === 'blocked') {
    return (
      <div className="max-w-lg" data-testid="lsc-admin-blocked">
        <div className="card space-y-3">
          <h1 className="text-xl font-semibold text-white">{LSC_ADMIN_BLOCKED_TITLE}</h1>
          <p className="text-sm text-secondary-300">{LSC_ADMIN_BLOCKED_MESSAGE}</p>
        </div>
      </div>
    );
  }

  if (accessState === 'error') {
    return (
      <div className="max-w-lg" data-testid="lsc-admin-access-error">
        <div className="card space-y-3">
          <h1 className="text-xl font-semibold text-white">Admin tools unavailable</h1>
          <p className="text-sm text-red-300" role="alert">
            {errorMessage}
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

export default LscAdminAccessGate;
